/**
 * Ponderación de la evidencia y veredicto.
 *
 * Reglas explícitas y deliberadamente simples (sin LLM), inspiradas en la
 * jerarquía de la evidencia y en GRADE. No pretende sustituir una revisión
 * sistemática: resume lo que dicen los abstracts analizados.
 */

import type { Classification, PaperResult, ReviewsSummary, Side, Verdict } from "./models";
import { applyReliability } from "./reliability";

// Cuánto pesa cada diseño de estudio (jerarquía de la evidencia)
// Incluye diseños de fuera de la biomedicina (economía, educación, ciencias sociales…)
export const DESIGN_WEIGHTS: Record<string, number> = {
  meta_analysis: 1.0,
  rct: 0.8,
  quasi_experimental: 0.6,
  cohort: 0.55,
  case_control: 0.4,
  observational: 0.35,
  cross_sectional: 0.3,
  modelling: 0.2,
  // Ciencias físicas (#61): medida, experimento u observación con datos nuevos (incluidas las
  // búsquedas que fijan límites), la evidencia primaria del campo
  measurement: 0.7,
  other: 0.2,
  narrative_review: 0.15,
  qualitative: 0.15,
  animal_in_vitro: 0.1,
};
// Estudios que no son en humanos pesan mucho menos al extrapolar a personas
export const NON_HUMAN_FACTOR = 0.3;

// Dirección de cada respuesta respecto a la pregunta (+1 = sí, -1 = no)
export const STANCE_SCORES: Record<string, number> = {
  yes: 1.0,
  partial: 0.5,
  inconclusive: 0.0,
  no_effect: -0.5,
  no: -1.0,
};
const STRONG_DESIGNS = new Set(["meta_analysis", "rct"]);
const MIN_RELEVANT = 3;

const round = (x: number, digits: number) => Math.round(x * 10 ** digits) / 10 ** digits;
const pct = (x: number) => `${Math.round(x * 100)}%`;
const sum = (xs: number[]) => xs.reduce((a, b) => a + b, 0);

const isRelevant = (c: Classification) => c.answer in STANCE_SCORES;

/** Peso esperado según la distribución de probabilidad del diseño. */
export function designWeight(c: Classification): number {
  const probs = Object.entries(c.design_probabilities ?? {});
  if (probs.length) {
    const total = sum(probs.map(([, p]) => p)) || 1;
    return sum(probs.map(([d, p]) => (DESIGN_WEIGHTS[d] ?? 0.2) * p)) / total;
  }
  return DESIGN_WEIGHTS[c.design] ?? 0.2;
}

/**
 * `humanFocus`: la pregunta trata de salud o conducta humana, así que los estudios en
 * animales o in vitro se extrapolan mal (en economía o física esa penalización no aplica).
 */
export function paperWeight(c: Classification, humanFocus = true): number {
  if (!isRelevant(c)) return 0; // not_addressed
  const humans = !humanFocus || c.humans >= 0.5 ? 1 : NON_HUMAN_FACTOR;
  return round(designWeight(c) * humans * c.relevance * c.answer_confidence, 4);
}

function side(answer: string): Side {
  const score = STANCE_SCORES[answer]!;
  return score > 0 ? "support" : score < 0 ? "against" : "neutral";
}

// Un lado "gana" si reúne al menos este múltiplo del peso del otro y un mínimo del total
export const DOMINANCE_RATIO = 1.3;
export const MIN_SIDE_SHARE = 0.35;
// Con mayoría no concluyente, un lado decide igualmente si reúne este mínimo y este múltiplo del otro
export const LOPSIDED_MIN_SHARE = 0.25;
export const LOPSIDED_RATIO = 4;

/**
 * Lado que domina la evidencia ponderada. Se decide por el reparto a favor / en contra, no
 * solo por la media: con mayoría de resultados nulos ("sin efecto" puntúa −0,5) la media
 * quedaba cerca de 0 y el titular decía "dividida" aunque el 74 % de la evidencia estuviera
 * en contra (vacunas y autismo, búsqueda exhaustiva).
 */
function dominant(shares: Record<Side, number>): Side | null {
  // Mayoría no concluyente, salvo que lo que sí concluye vaya casi todo en un sentido: en física
  // muchos límites sin detección son «no concluyentes» y ahogaban la evidencia clara (masa del
  // neutrino: toda la evidencia decidida, a favor)
  const lopsided = (a: number, b: number) => a >= LOPSIDED_MIN_SHARE && a >= LOPSIDED_RATIO * b;
  if (shares.neutral >= 0.5) {
    if (lopsided(shares.support, shares.against)) return "support";
    if (lopsided(shares.against, shares.support)) return "against";
    return "neutral";
  }
  if (shares.support >= MIN_SIDE_SHARE && shares.support >= DOMINANCE_RATIO * shares.against) return "support";
  if (shares.against >= MIN_SIDE_SHARE && shares.against >= DOMINANCE_RATIO * shares.support) return "against";
  return null;
}

function headline(
  score: number,
  side: Side | null,
  byAnswer: Record<string, number>,
): [string, string] {
  if (side === "neutral") {
    // La mayoría no concluye nada: ausencia de pruebas, no "evidencia dividida"
    return ["no_evidence", "No hay pruebas sólidas: la mayoría de estudios no son concluyentes"];
  }
  if (side === "support") {
    // Umbral por encima de 0,5 para que un conjunto de solo "parcial" no se lea como "Sí"
    return score >= 0.6 ? ["yes", "Sí"] : ["likely_yes", "Probablemente sí, con matices"];
  }
  if (side === "against") {
    if (byAnswer.no_effect! >= byAnswer.no!) return ["no_effect", "Probablemente no: la mayoría no encuentra efecto"];
    return score <= -0.6 ? ["no", "No"] : ["likely_no", "Probablemente no"];
  }
  return ["mixed", "La evidencia está dividida"];
}

/**
 * Certeza en el veredicto. Como en GRADE, una evidencia observacional muy consistente
 * y con resultados claros (p. ej. tabaco y cáncer, donde no hay ensayos) también puede
 * dar certeza alta. `clearShare` = peso de las respuestas sin matices ("partial" no cuenta).
 */
function certainty(
  effectiveN: number,
  agreement: number,
  strongAgreeing: number,
  nAgreeing: number,
  clearShare: number,
): string {
  if (
    agreement >= 0.75 &&
    ((effectiveN >= 4 && strongAgreeing >= 2) ||
      (effectiveN >= 2 && agreement >= 0.9 && nAgreeing >= 8 && clearShare >= 0.8))
  ) {
    return "alta";
  }
  if (
    agreement >= 0.6 &&
    ((effectiveN >= 2 && strongAgreeing >= 1) || (agreement >= 0.85 && nAgreeing >= 5))
  ) {
    return "moderada";
  }
  if (effectiveN >= 1 && agreement >= 0.5) return "baja";
  return "muy baja";
}

export function summarizeReviews(results: PaperResult[]): ReviewsSummary {
  const counts: Record<string, number> = Object.fromEntries(Object.keys(STANCE_SCORES).map((k) => [k, 0]));
  for (const r of results) {
    const c = r.classification;
    if (c && c.design === "meta_analysis" && isRelevant(c)) counts[c.answer]! += 1;
  }
  return { total: sum(Object.values(counts)), counts };
}

// Con los estudios divididos y al menos estas revisiones sistemáticas, el titular sigue a las revisiones
export const REVIEWS_TIEBREAK_MIN = 10;

/** Veredicto con las correcciones de fiabilidad (ver reliability.ts). */
export function assess(results: PaperResult[]): Verdict {
  const { results: adjusted, corrections } = applyReliability(results, (r) => !!r.classification && isRelevant(r.classification));
  const v = assessWeighted(adjusted);
  v.corrections = corrections;
  if (v.label === "mixed" && v.reviews.total >= REVIEWS_TIEBREAK_MIN) {
    const reviews = adjusted.filter((r) => r.classification?.design === "meta_analysis" && isRelevant(r.classification));
    const rv = assessWeighted(reviews);
    if (rv.label !== "mixed" && rv.label !== "insufficient") {
      v.label = rv.label;
      v.headline = rv.headline;
      v.certainty = rv.certainty;
      corrections.push({
        kind: "reviews_tiebreak",
        text:
          `Los estudios están divididos, así que el titular sigue a las ${reviews.length} revisiones sistemáticas ` +
          `(${pct(rv.shares.support)} a favor, ${pct(rv.shares.against)} en contra o sin efecto, ${pct(rv.shares.neutral)} no concluyente).`,
        papers: reviews.map((r) => r.paper.source_id),
        factor: 1,
        params: { n: reviews.length, support: pct(rv.shares.support), against: pct(rv.shares.against), neutral: pct(rv.shares.neutral) },
      });
    }
  }
  return v;
}

function assessWeighted(results: PaperResult[]): Verdict {
  const relevant = results
    .map((r) => r.classification)
    .filter((c): c is Classification => !!c && isRelevant(c));
  const reviews = summarizeReviews(results);
  if (relevant.length < MIN_RELEVANT) {
    return {
      label: "insufficient",
      headline: "Evidencia insuficiente",
      explanation:
        `Solo ${relevant.length} de los papers analizados responden a la pregunta. ` +
        "Prueba con otros términos de búsqueda o con más papers.",
      certainty: "muy baja",
      score: 0,
      shares: { support: 0, neutral: 0, against: 0 },
      effective_n: round(sum(relevant.map((c) => c.weight)), 2),
      n_relevant: relevant.length,
      reviews,
      corrections: [],
    };
  }

  const totalW = sum(relevant.map((c) => c.weight)) || 1e-9;
  const score = sum(relevant.map((c) => c.weight * STANCE_SCORES[c.answer]!)) / totalW;
  const shares: Record<Side, number> = { support: 0, neutral: 0, against: 0 };
  const byAnswer: Record<string, number> = Object.fromEntries(Object.keys(STANCE_SCORES).map((k) => [k, 0]));
  for (const c of relevant) {
    shares[side(c.answer)] += c.weight / totalW;
    byAnswer[c.answer]! += c.weight / totalW;
  }

  const winner = dominant(shares);
  const [label, title] = headline(score, winner, byAnswer);
  // Sin lado dominante, la certeza se mide sobre el lado con más peso
  const dom: Side = winner ?? (["support", "against", "neutral"] as const).reduce((a, b) => (shares[b] > shares[a] ? b : a));
  const agreeing = relevant.filter((c) => side(c.answer) === dom);
  const strongAgreeing = agreeing.filter((c) => STRONG_DESIGNS.has(c.design)).length;
  const agreeingW = sum(agreeing.map((c) => c.weight)) || 1e-9;
  const clearShare = sum(agreeing.filter((c) => c.answer !== "partial").map((c) => c.weight)) / agreeingW;

  const nStrong = relevant.filter((c) => STRONG_DESIGNS.has(c.design)).length;
  return {
    label,
    headline: title,
    explanation:
      `${relevant.length} papers responden a la pregunta (${nStrong} son revisiones ` +
      `sistemáticas o ensayos aleatorizados). Ponderando por calidad: ` +
      `${pct(shares.support)} a favor, ${pct(shares.against)} en contra o sin efecto, ` +
      `${pct(shares.neutral)} no concluyente.`,
    certainty: certainty(totalW, shares[dom], strongAgreeing, agreeing.length, clearShare),
    score: round(score, 3),
    shares: { support: round(shares.support, 3), neutral: round(shares.neutral, 3), against: round(shares.against, 3) },
    effective_n: round(totalW, 2),
    n_relevant: relevant.length,
    n_strong: nStrong,
    reviews,
    corrections: [],
  };
}
